import { useReactTable } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { CopyTextButton } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  ExtendedSortingState,
  Heading,
  ServerDataTable,
  Stack,
  TableSearch,
  makeDefaultSortableColumnDef,
  useDataTableForQuery,
} from '@eluve/components';
import { UsersBoolExp, UsersOrderBy } from '@eluve/graphql-types';
import { ResultOf, graphql } from '@eluve/graphql.tada';

import { UpdateUserAction } from './UpdateUserAction';
import {
  UserTemplateCreatorToggle,
  UserTemplatePublisherToggle,
} from './UserPermissionToggles';

export const getUsersQuery = graphql(`
  query getUsers(
    $filter: UsersBoolExp
    $offset: Int
    $limit: Int
    $orderBy: [UsersOrderBy!]
  ) {
    users(where: $filter, offset: $offset, limit: $limit, orderBy: $orderBy) {
      id
      firstName
      lastName
      email
      createdAt
      isPromptTemplateCreator
      isTemplatePublisher
      tenants(orderBy: { lastAccessedAt: DESC_NULLS_LAST }, limit: 1) {
        tenantId
        userId
        lastAccessedAt
      }
    }
    usersAggregate(where: $filter) {
      aggregate {
        count
      }
    }
  }
`);

export type UserRow = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  lastAccessedAt?: string;
  isPromptTemplateCreator: boolean;
  isTemplatePublisher: boolean;
};

const cols = new ColDefBuilder<UserRow>()
  .colDef({
    id: 'id',
    accessorKey: 'id',
    header: 'Id',
    cell: ({ row }) => <CopyTextButton copyText={row.original.id} />,
  })
  .defaultSortable('firstName', 'First Name')
  .defaultSortable('lastName', 'Last Name')
  .defaultSortable('email', 'Email')
  .dateSortable('createdAt', 'Created At')
  .dateSortable('lastAccessedAt', 'Last Accessed At')
  .colDef(
    makeDefaultSortableColumnDef('isPromptTemplateCreator', {
      label: 'Is Template Creator',
      cellRenderer: (row) => <UserTemplateCreatorToggle userId={row.id} />,
    }),
  )
  .colDef(
    makeDefaultSortableColumnDef('isTemplatePublisher', {
      label: 'Is Template Publisher',
      cellRenderer: (row) => <UserTemplatePublisherToggle userId={row.id} />,
    }),
  )
  .colDef({
    header: 'Actions',
    cell: ({ row }) => (
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <UpdateUserAction
          userId={row.original.id}
          defaultValues={row.original}
        />
        <Link to={`/admin/users/${row.original.id}`}>
          <Button>View</Button>
        </Link>
      </Stack>
    ),
  })
  .build();

export const convertToRows = (
  results: ResultOf<typeof getUsersQuery>,
): UserRow[] => {
  return (results?.users ?? []).map<UserRow>((user) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    createdAt: user.createdAt,
    lastAccessedAt: user.tenants[0]?.lastAccessedAt ?? undefined,
    isPromptTemplateCreator: user.isPromptTemplateCreator,
    isTemplatePublisher: user.isTemplatePublisher,
  }));
};

/**
 * Builds the Hasura query variables for {@link getUsersQuery} from the data
 * table's search params. Extracted as a pure, module-level function so the
 * filter/orderBy construction (notably the `lastAccessedAt` aggregate sort)
 * can be unit-tested without rendering the table.
 */
export const buildUsersQueryVariables = ({
  offset,
  limit,
  search,
  sorting,
}: {
  offset: number;
  limit: number;
  search: string | null;
  sorting: ExtendedSortingState<UserRow>;
}): {
  filter: UsersBoolExp;
  offset: number;
  limit: number;
  orderBy: UsersOrderBy[] | null;
} => {
  const filterConditions: UsersBoolExp[] = [];

  if (search) {
    // A valid uuid can only be an `id` (it won't match names/email), so match
    // on `id` exactly. `id` is a uuid column — `_ilike` isn't valid and `_eq`
    // errors on non-uuid input, hence the branch.
    if (z.string().uuid().safeParse(search).success) {
      filterConditions.push({ id: { _eq: search } });
    } else {
      filterConditions.push({
        _or: [
          { email: { _ilike: `%${search}%` } },
          { firstName: { _ilike: `%${search}%` } },
          { lastName: { _ilike: `%${search}%` } },
        ],
      });
    }
  }

  return {
    filter: { _and: filterConditions },
    offset,
    limit,
    orderBy:
      sorting && sorting.length
        ? sorting.map(({ desc, id }): UsersOrderBy => {
            const direction = desc ? 'DESC' : 'ASC';

            if (id === 'lastAccessedAt') {
              // Sort by the user's most-recent tenant access (the value
              // shown in the column). NULLS LAST in both directions so
              // never-accessed users sink to the bottom — otherwise DESC
              // buries the most-recent users behind every null row.
              return {
                tenantsAggregate: {
                  max: {
                    lastAccessedAt: desc ? 'DESC_NULLS_LAST' : 'ASC_NULLS_LAST',
                  },
                },
              };
            }

            return { [id]: direction };
          })
        : null,
  };
};

const defaultSort: ExtendedSortingState<UserRow> = [
  { id: 'createdAt', desc: true },
];

export const UsersTable: React.FC = () => {
  const { reactTableOptions, isPending, data, rows, search, setSearch } =
    useDataTableForQuery({
      query: getUsersQuery,
      convertToRows,
      defaultSort,
      convertSearchParamsToVariables: ({ offset, limit, search, sorting }) =>
        buildUsersQueryVariables({ offset, limit, search, sorting }),
    });

  const rowCount = data?.usersAggregate?.aggregate?.count;

  const table = useReactTable({
    data: rows,
    columns: cols,
    rowCount,
    ...reactTableOptions,
  });

  return (
    <div>
      <div className="my-2 py-2">
        <Heading>All Users</Heading>
      </div>
      <Stack gap={2} className="rounded-md border">
        <TableSearch
          value={search ?? ''}
          onChange={setSearch}
          placeholder="Search by name or email..."
        />
        <ServerDataTable<UserRow>
          table={table}
          isPending={isPending}
          highlightTerm={search ?? ''}
        />
      </Stack>
    </div>
  );
};
